import * as tslib_1 from "tslib";
import { OnDestroy, OnInit, } from '@angular/core';
import { FormBuilder, FormControl, Validators, } from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiExecutionSettingsComponent } from '@components';
import { enumMapper, RobotTypeAttended, UserType, } from '@models';
import { SettingService } from '@services';
import { identifier } from '@uipath/angular/utilities';
import UiValidators, { UI_ROBOT_USERNAME_MAX_LENGTH, UI_ROBOT_USERNAME_REGEX, } from '@validators';
import cloneDeep from 'lodash-es/cloneDeep';
import { of, ReplaySubject, Subject, } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil, } from 'rxjs/operators';
var RobotFormControl;
(function (RobotFormControl) {
    RobotFormControl["isExternalLicensed"] = "isExternalLicensed";
    RobotFormControl["userName"] = "userName";
    RobotFormControl["type"] = "type";
    RobotFormControl["executionSettings"] = "executionSettings";
})(RobotFormControl || (RobotFormControl = {}));
var EXECUTION_SETTINGS_PERMISSIONS = 'Settings.Edit|Robots.Create|Robots.Edit';
var ProvisionRobotComponent = /** @class */ (function () {
    function ProvisionRobotComponent(_settingsService, _formBuilder, _appState) {
        this._settingsService = _settingsService;
        this._formBuilder = _formBuilder;
        this._appState = _appState;
        this.className = 'ui-user-provision-robot';
        this.RobotFormControl = RobotFormControl;
        this.UserType = UserType;
        this.RobotTypeAttended = RobotTypeAttended;
        this.typeList = enumMapper(RobotTypeAttended, {
            resourceKey: 'RobotType',
        }).map(function (entry) { return (tslib_1.__assign({}, entry, { value: Number(entry.value) })); });
        this.robotTypeId = identifier();
        this.executionSettingsConfig$ = new ReplaySubject(1);
        this.loadingSettings$ = new Subject();
        this._destroyed$ = new Subject();
        this._userType = UserType.User;
        this._enabled = true;
        this._userNameControl = new FormControl(undefined, [
            Validators.maxLength(UI_ROBOT_USERNAME_MAX_LENGTH),
            UiValidators.regex(UI_ROBOT_USERNAME_REGEX, true),
            UiValidators.required(),
        ]);
    }
    Object.defineProperty(ProvisionRobotComponent.prototype, "enabled", {
        get: function () {
            return this._enabled;
        },
        set: function (enabled) {
            this._enabled = enabled;
            if (!this.form) {
                return;
            }
            if (enabled) {
                this.form.enable();
            }
            else {
                this.form.disable();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProvisionRobotComponent.prototype, "userType", {
        get: function () { return this._userType; },
        set: function (userType) {
            this._userType = userType;
            if (!this.form) {
                return;
            }
            if (!this.hasExecutionSettings) {
                this.form.removeControl(RobotFormControl.executionSettings);
            }
            // reset group license type to development if none is selected
            // for anything else than AD User
            if (this._userType !== UserType.DirectoryUser &&
                !this.form.get(RobotFormControl.type).value) {
                this.form.get(RobotFormControl.type).setValue(RobotTypeAttended.Development);
            }
            // we only have the username on standard users
            if ([UserType.DirectoryGroup, UserType.DirectoryUser, UserType.Robot].includes(this.userType)) {
                this.form.removeControl(RobotFormControl.userName);
            }
            else {
                this.form.addControl(RobotFormControl.userName, this._userNameControl);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProvisionRobotComponent.prototype, "executionSettingsComponent", {
        set: function (value) {
            if (!value ||
                !!this.form.get(RobotFormControl.executionSettings) ||
                !this.hasExecutionSettings) {
                return;
            }
            this.form.addControl(RobotFormControl.executionSettings, value.formGroup);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ProvisionRobotComponent.prototype, "hasExecutionSettings", {
        get: function () {
            return [UserType.User, UserType.DirectoryUser].includes(this.userType);
        },
        enumerable: true,
        configurable: true
    });
    ProvisionRobotComponent.prototype.ngOnInit = function () {
        var _a;
        var _this = this;
        var robot = cloneDeep(this.robot) || {};
        robot.type =
            robot.type != null
                ? robot.type
                : this._userType === UserType.DirectoryUser ? null : RobotTypeAttended.Development;
        robot.executionSettings = robot.executionSettings || {};
        robot.isExternalLicensed = robot.isExternalLicensed || false;
        this.form = this._formBuilder.group((_a = {},
            _a[RobotFormControl.type] = robot.type,
            _a));
        this.form.addControl(RobotFormControl.isExternalLicensed, new FormControl(robot.isExternalLicensed));
        if (this.userType === UserType.User) {
            this._userNameControl.setValue(robot.userName);
            this.form.addControl(RobotFormControl.userName, this._userNameControl);
        }
        // wait for the execution settings configuration to load
        this.form.markAsPending();
        var guardedExecutionSettings$ = this._appState.user
            .permissions.isGranted(EXECUTION_SETTINGS_PERMISSIONS)
            .pipe(switchMap(function (isGranted) { return isGranted
            ? _this._settingsService.getExecutionSettingsConfiguration(1 /* Robot */)
            : of({ configuration: [] }); }));
        guardedExecutionSettings$
            .pipe(finalize(function () { return _this.loadingSettings$.next(false); }), catchError(function () { return of({ configuration: [] }); }), takeUntil(this._destroyed$)).subscribe(function (data) {
            _this.executionSettingsConfig$.next(data.configuration);
            if (_this.form.status === 'PENDING') {
                _this.form.markAsPristine();
            }
            _this.enabled = _this._enabled;
        });
    };
    ProvisionRobotComponent.prototype.ngOnDestroy = function () {
        this._destroyed$.next();
        this._destroyed$.complete();
    };
    return ProvisionRobotComponent;
}());
export { ProvisionRobotComponent };
